import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  CreditCard, DollarSign, TrendingUp, ArrowUpCircle, ArrowDownCircle, 
  RefreshCw, Users, Calendar, CheckCircle, XCircle, Clock, Wallet,
  Building, AlertCircle
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentStripeDashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [payments, setPayments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const [dashRes, paymentsRes, subsRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/agent/stripe/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/stripe/payments?limit=20`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/stripe/subscriptions?limit=20`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/stripe/payouts?limit=20`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDashboard(dashRes.data);
      setPayments(paymentsRes.data.payments || []);
      setSubscriptions(subsRes.data.subscriptions || []);
      setPayouts(payoutsRes.data.payouts || []);
    } catch (error) {
      toast.error("Failed to load Stripe data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      succeeded: "bg-green-100 text-green-800",
      active: "bg-green-100 text-green-800",
      paid: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      in_transit: "bg-blue-100 text-blue-800",
      trialing: "bg-purple-100 text-purple-800",
      canceled: "bg-red-100 text-red-800",
      failed: "bg-red-100 text-red-800"
    };
    return <Badge className={styles[status] || "bg-gray-100 text-gray-800"}>{status}</Badge>;
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AgentLayout>
    );
  }

  if (!dashboard?.connected) {
    return (
      <AgentLayout>
        <Card className="card-organic max-w-lg mx-auto mt-8">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Stripe Not Connected</h2>
            <p className="text-muted-foreground mb-4">
              Configure your Stripe API key in Settings to view payment data.
            </p>
            <Button onClick={() => window.location.href = '/agent/settings'}>
              Go to Settings
            </Button>
          </CardContent>
        </Card>
      </AgentLayout>
    );
  }

  // Show error state but still render the dashboard with available data
  const hasError = dashboard?.error;

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>
              <CreditCard className="inline h-8 w-8 mr-3 text-primary" />
              Stripe Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Real-time payment data and analytics from Stripe
            </p>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Error Banner */}
        {hasError && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Stripe API Issue</p>
                <p className="text-sm text-amber-700">{dashboard.error}. Please check your API key in Settings.</p>
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                className="ml-auto"
                onClick={() => window.location.href = '/agent/settings'}
              >
                Go to Settings
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card 
            className="card-organic bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveTab("payouts")}
            data-testid="balance-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700 font-medium">Available Balance</p>
                  <p className="text-3xl font-bold text-green-800">
                    ${dashboard.balance?.available?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <Wallet className="h-10 w-10 text-green-500 opacity-50" />
              </div>
              <p className="text-xs text-green-600 mt-2">Click to view payouts →</p>
            </CardContent>
          </Card>
          <Card 
            className="card-organic bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveTab("payments")}
            data-testid="pending-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700 font-medium">Pending Balance</p>
                  <p className="text-3xl font-bold text-blue-800">
                    ${dashboard.balance?.pending?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <Clock className="h-10 w-10 text-blue-500 opacity-50" />
              </div>
              <p className="text-xs text-blue-600 mt-2">Click to view payments →</p>
            </CardContent>
          </Card>
          <Card 
            className="card-organic bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveTab("subscriptions")}
            data-testid="mrr-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700 font-medium">Monthly Recurring Revenue</p>
                  <p className="text-3xl font-bold text-purple-800">
                    ${dashboard.subscriptions?.mrr?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <TrendingUp className="h-10 w-10 text-purple-500 opacity-50" />
              </div>
              <p className="text-xs text-purple-600 mt-2">Click to view subscriptions →</p>
            </CardContent>
          </Card>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card 
            className="card-organic cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setActiveTab("payments")}
            data-testid="total-payments-card"
          >
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground">Total Payments</p>
              <p className="text-2xl font-bold">{dashboard.payments?.total || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className="card-organic cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setActiveTab("payments")}
            data-testid="this-month-card"
          >
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground">This Month</p>
              <p className="text-2xl font-bold text-green-600">{dashboard.payments?.this_month || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className="card-organic cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setActiveTab("payments")}
            data-testid="revenue-card"
          >
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <p className="text-2xl font-bold">${dashboard.payments?.amount_total?.toFixed(0) || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className="card-organic cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setActiveTab("subscriptions")}
            data-testid="active-subs-card"
          >
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground">Active Subs</p>
              <p className="text-2xl font-bold text-blue-600">{dashboard.subscriptions?.active || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className="card-organic cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setActiveTab("payments")}
            data-testid="customers-card"
          >
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground">Customers</p>
              <p className="text-2xl font-bold">{dashboard.customers?.total || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className="card-organic cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setActiveTab("payouts")}
            data-testid="payouts-card"
          >
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground">Payouts</p>
              <p className="text-2xl font-bold">${dashboard.payouts?.amount_total?.toFixed(0) || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for detailed data */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="payouts">Payouts</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Payments */}
              <Card className="card-organic">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowDownCircle className="h-5 w-5 text-green-500" />
                    Recent Payments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard.recent_payments?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recent_payments.slice(0, 5).map(payment => (
                        <div key={payment.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div>
                            <p className="font-medium">${payment.amount?.toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(payment.created).toLocaleDateString()}
                            </p>
                          </div>
                          {getStatusBadge(payment.status)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">No recent payments</p>
                  )}
                </CardContent>
              </Card>

              {/* Recent Payouts */}
              <Card className="card-organic">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowUpCircle className="h-5 w-5 text-blue-500" />
                    Recent Payouts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard.recent_payouts?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recent_payouts.slice(0, 5).map(payout => (
                        <div key={payout.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div>
                            <p className="font-medium">${payout.amount?.toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">
                              {payout.arrival_date ? `Arrives ${new Date(payout.arrival_date).toLocaleDateString()}` : 'Processing'}
                            </p>
                          </div>
                          {getStatusBadge(payout.status)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">No recent payouts</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="mt-4">
            <Card className="card-organic">
              <CardHeader>
                <CardTitle>All Payments</CardTitle>
                <CardDescription>Complete payment history from Stripe</CardDescription>
              </CardHeader>
              <CardContent>
                {payments.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Amount</th>
                          <th>Customer</th>
                          <th>Status</th>
                          <th>Method</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map(payment => (
                          <tr key={payment.id}>
                            <td className="font-mono text-xs">{payment.id?.slice(-8)}</td>
                            <td className="font-medium">${payment.amount?.toFixed(2)}</td>
                            <td>{payment.customer_email || payment.customer_id || '-'}</td>
                            <td>{getStatusBadge(payment.status)}</td>
                            <td className="capitalize">{payment.payment_method || '-'}</td>
                            <td>{new Date(payment.created).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No payments found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" className="mt-4">
            <Card className="card-organic">
              <CardHeader>
                <CardTitle>Subscriptions</CardTitle>
                <CardDescription>Active and past subscriptions</CardDescription>
              </CardHeader>
              <CardContent>
                {subscriptions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Customer</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Period</th>
                          <th>Cancel at End</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptions.map(sub => (
                          <tr key={sub.id}>
                            <td className="font-mono text-xs">{sub.id?.slice(-8)}</td>
                            <td>{sub.customer_email || sub.customer_id || '-'}</td>
                            <td className="font-medium">${sub.amount?.toFixed(2)}/{sub.interval}</td>
                            <td>{getStatusBadge(sub.status)}</td>
                            <td className="text-xs">
                              {new Date(sub.current_period_start).toLocaleDateString()} - {new Date(sub.current_period_end).toLocaleDateString()}
                            </td>
                            <td>
                              {sub.cancel_at_period_end ? (
                                <Badge className="bg-amber-100 text-amber-800">Yes</Badge>
                              ) : (
                                <span className="text-muted-foreground">No</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No subscriptions found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts" className="mt-4">
            <Card className="card-organic">
              <CardHeader>
                <CardTitle>Payouts to Bank</CardTitle>
                <CardDescription>Money transferred to your bank account</CardDescription>
              </CardHeader>
              <CardContent>
                {payouts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Method</th>
                          <th>Arrival Date</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payouts.map(payout => (
                          <tr key={payout.id}>
                            <td className="font-mono text-xs">{payout.id?.slice(-8)}</td>
                            <td className="font-medium">${payout.amount?.toFixed(2)}</td>
                            <td>{getStatusBadge(payout.status)}</td>
                            <td className="capitalize">{payout.method}</td>
                            <td>{payout.arrival_date ? new Date(payout.arrival_date).toLocaleDateString() : '-'}</td>
                            <td>{new Date(payout.created).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No payouts found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AgentLayout>
  );
}
